import React, { useState, useEffect, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';

const SearchBar = ({ routes, onSearch, searchValue, onSearchChange }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const suggestionsRef = useRef(null);

  useEffect(() => {
    if (searchValue?.trim()?.length > 0) {
      const filtered = routes?.filter(route =>
        route?.name?.toLowerCase()?.includes(searchValue?.toLowerCase()) ||
        route?.startPoint?.toLowerCase()?.includes(searchValue?.toLowerCase()) ||
        route?.endPoint?.toLowerCase()?.includes(searchValue?.toLowerCase())
      )?.slice(0, 5);
      setSuggestions(filtered);
      setShowSuggestions(filtered?.length > 0);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  }, [searchValue, routes]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (suggestionsRef?.current && !suggestionsRef?.current?.contains(event?.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleKeyDown = (e) => {
    if (!showSuggestions) return;

    switch (e?.key) {
      case 'ArrowDown':
        e?.preventDefault();
        setSelectedIndex(prev => 
          prev < suggestions?.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e?.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e?.preventDefault();
        if (selectedIndex >= 0 && suggestions?.[selectedIndex]) {
          handleSuggestionClick(suggestions?.[selectedIndex]);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        setSelectedIndex(-1);
        break;
      default:
        break;
    }
  };

  const handleSuggestionClick = (route) => {
    onSearchChange(route?.name);
    onSearch(route?.name);
    setShowSuggestions(false);
    setSelectedIndex(-1);
  };

  const handleClear = () => {
    onSearchChange('');
    onSearch('');
    setShowSuggestions(false);
    setSelectedIndex(-1);
  };

  return (
    <div className="relative w-full" ref={suggestionsRef}>
      <div className="relative">
        <Input
          type="search"
          placeholder="Rota adı, başlangıç veya bitiş noktası ara..."
          value={searchValue}
          onChange={(e) => onSearchChange(e?.target?.value)}
          onKeyDown={handleKeyDown}
          className="w-full pl-12 pr-12"
        />
        <div className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none">
          <Icon name="Search" size={20} color="var(--color-muted-foreground)" />
        </div>
        {searchValue && (
          <button
            onClick={handleClear}
            className="absolute right-4 top-1/2 -translate-y-1/2 flex items-center justify-center w-6 h-6 rounded-full hover:bg-muted transition-smooth active-press"
            aria-label="Temizle"
          >
            <Icon name="X" size={16} color="var(--color-muted-foreground)" />
          </button>
        )}
      </div>
      {showSuggestions && suggestions?.length > 0 && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-card border border-border rounded-lg shadow-xl z-50 max-h-80 overflow-y-auto">
          {suggestions?.map((route, index) => (
            <button
              key={route?.id}
              onClick={() => handleSuggestionClick(route)}
              className={`w-full text-left p-4 transition-smooth hover:bg-muted border-b border-border last:border-b-0 ${
                index === selectedIndex ? 'bg-muted' : ''
              }`}
            >
              <div className="flex items-start gap-3">
                <div className="flex items-center justify-center w-10 h-10 bg-primary/10 rounded-lg flex-shrink-0">
                  <Icon name="Bus" size={20} color="var(--color-primary)" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm md:text-base font-semibold text-foreground mb-1 truncate">
                    {route?.name}
                  </p>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Icon name="MapPin" size={12} color="var(--color-muted-foreground)" />
                    <span className="truncate">{route?.startPoint} → {route?.endPoint}</span>
                  </div>
                  <div className="flex items-center gap-3 mt-2">
                    <span className="text-xs text-muted-foreground">
                      {route?.distanceKm} km
                    </span>
                    <span className="text-xs text-primary font-medium">
                      ~{route?.estimatedMinutes} dk
                    </span>
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default SearchBar;